import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Check, 
  ChevronRight, 
  ChevronLeft, 
  Wifi, 
  User, 
  CreditCard, 
  FileCheck,
  Loader2,
  Zap,
  Shield
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Produkt', icon: Wifi },
  { id: 2, title: 'Kundendaten', icon: User },
  { id: 3, title: 'SEPA', icon: CreditCard },
  { id: 4, title: 'Bestätigung', icon: FileCheck }
];

const NewOrder = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [products, setProducts] = useState([]);
  const [addons, setAddons] = useState([]);
  const [legalDocs, setLegalDocs] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    product_id: '',
    contract_type: '24m',
    addons: [],
    include_router: true,
    custom_setup_fee: null, // null = use default, number = custom amount
    customer: {
      salutation: 'Herr',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      birth_date: '',
      service_street: '',
      service_house_number: '',
      service_postal_code: '',
      service_city: '',
      billing_same_as_service: true,
      billing_street: '',
      billing_house_number: '',
      billing_postal_code: '',
      billing_city: '',
      company_name: ''
    },
    sepa: {
      account_holder: '',
      iban: '',
      bic: ''
    },
    preferred_date: '',
    notes: '',
    agb_accepted: false,
    widerruf_accepted: false,
    datenschutz_accepted: false,
    marketing_email: false,
    marketing_phone: false
  });

  const loadProducts = useCallback(async () => {
    try {
      const [productsRes, addonsRes, legalDocsRes] = await Promise.all([
        api.getProducts(),
        api.getAddons(),
        api.getLegalDocuments()
      ]);
      setProducts(productsRes.data);
      setAddons(addonsRes.data);
      setLegalDocs(legalDocsRes.data);
      
      // Automatically add included addons to the order
      const includedAddonIds = addonsRes.data
        .filter(addon => addon.included)
        .map(addon => addon.id);
      if (includedAddonIds.length > 0) {
        setFormData(prev => ({
          ...prev,
          addons: [...new Set([...prev.addons, ...includedAddonIds])]
        }));
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Produkte');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const toggleAddon = (addonId) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.includes(addonId)
        ? prev.addons.filter(id => id !== addonId)
        : [...prev.addons, addonId]
    }));
  };

  const calculateTotals = () => {
    const product = products.find(p => p.id === formData.product_id);
    if (!product) return { monthly: 0, setup: 0, defaultSetup: 0 };

    let monthly = product.monthly_price;
    formData.addons.forEach(addonId => {
      const addon = addons.find(a => a.id === addonId);
      if (addon) monthly += addon.monthly_price;
    });

    // Default setup fee based on contract type
    const defaultSetup = formData.contract_type === '24m' 
      ? product.setup_fee_24m 
      : product.setup_fee_no_contract;

    // Use custom setup fee if set, otherwise use default
    const setup = formData.custom_setup_fee !== null 
      ? formData.custom_setup_fee 
      : defaultSetup;

    return { monthly, setup, defaultSetup };
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        return formData.product_id !== '';
      case 2:
        const c = formData.customer;
        return c.first_name && c.last_name && c.email && c.phone && 
               c.birth_date && c.service_street && c.service_house_number &&
               c.service_postal_code && c.service_city;
      case 3:
        return formData.sepa.account_holder && formData.sepa.iban;
      case 4:
        return formData.agb_accepted && formData.widerruf_accepted && formData.datenschutz_accepted;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 4));
    } else {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      toast.error('Bitte akzeptieren Sie alle erforderlichen Bedingungen');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.createOrder(formData);
      toast.success(`Bestellung ${response.data.order_number} erfolgreich erstellt`);
      navigate(`/orders/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Bestellung');
    } finally {
      setSubmitting(false);
    }
  };

  const totals = calculateTotals();
  const selectedProduct = products.find(p => p.id === formData.product_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F47920]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* Stepper */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((s, index) => (
            <div key={s.id} className="flex items-center flex-1">
              <div className="stepper-step flex-shrink-0">
                <div className={`stepper-dot ${
                  step > s.id ? 'completed' : step === s.id ? 'current' : 'upcoming'
                }`}>
                  {step > s.id ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <s.icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`text-xs mt-2 font-medium ${
                  step >= s.id ? 'text-slate-900' : 'text-slate-400'
                }`}>
                  {s.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`stepper-line ${step > s.id ? 'completed' : 'upcoming'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <Card className="border-0 shadow-lg">
        {/* Step 1: Product Selection */}
        {step === 1 && (
          <>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Produkt auswählen</CardTitle>
              <CardDescription>Wählen Sie das passende SafeConnect-Paket für Ihren Kunden</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Products */}
              <div className="grid gap-4">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className={`product-card p-6 rounded-xl border-2 cursor-pointer ${
                      formData.product_id === product.id 
                        ? 'border-[#F47920] bg-[#F47920]/5' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => updateFormData('product_id', product.id)}
                    data-testid={`product-${product.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            formData.product_id === product.id ? 'bg-[#F47920]' : 'bg-slate-100'
                          }`}>
                            <Zap className={`w-5 h-5 ${
                              formData.product_id === product.id ? 'text-white' : 'text-slate-500'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg text-slate-900">{product.name}</h3>
                            <p className="text-sm text-slate-500">{product.description}</p>
                          </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[#F47920]">{product.download_speed}</span>
                            <span className="text-slate-500">Download</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[#F47920]">{product.upload_speed}</span>
                            <span className="text-slate-500">Upload</span>
                          </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {product.features.slice(0, 3).map((feature) => (
                            <span key={feature} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                              <Check className="w-3 h-3 text-emerald-500" />
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-2xl font-bold text-slate-900">{formatCurrency(product.monthly_price)}</p>
                        <p className="text-sm text-slate-500">pro Monat</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Contract Type */}
              {formData.product_id && (
                <div className="space-y-4 pt-6 border-t">
                  <Label className="text-base font-semibold">Vertragslaufzeit</Label>
                  <RadioGroup
                    value={formData.contract_type}
                    onValueChange={(value) => updateFormData('contract_type', value)}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  >
                    <label className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.contract_type === '24m' ? 'border-[#F47920] bg-[#F47920]/5' : 'border-slate-200'
                    }`}>
                      <RadioGroupItem value="24m" id="24m" className="sr-only" />
                      <div className="flex-1">
                        <p className="font-medium">24 Monate Laufzeit</p>
                        <p className="text-sm text-slate-500">
                          Einmalig: {formatCurrency(selectedProduct?.setup_fee_24m || 0)}
                        </p>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        formData.contract_type === '24m' ? 'border-[#F47920] bg-[#F47920]' : 'border-slate-300'
                      }`}>
                        {formData.contract_type === '24m' && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </label>
                    <label className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.contract_type === 'no_contract' ? 'border-[#F47920] bg-[#F47920]/5' : 'border-slate-200'
                    }`}>
                      <RadioGroupItem value="no_contract" id="no_contract" className="sr-only" />
                      <div className="flex-1">
                        <p className="font-medium">Ohne Mindestlaufzeit</p>
                        <p className="text-sm text-slate-500">
                          Einmalig: {formatCurrency(selectedProduct?.setup_fee_no_contract || 0)}
                        </p>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        formData.contract_type === 'no_contract' ? 'border-[#F47920] bg-[#F47920]' : 'border-slate-300'
                      }`}>
                        {formData.contract_type === 'no_contract' && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              )}

              {/* Addons */}
              {formData.product_id && (
                <div className="space-y-4 pt-6 border-t">
                  <Label className="text-base font-semibold">Zusatzoptionen</Label>
                  <div className="grid gap-3">
                    {addons.map((addon) => (
                      <label
                        key={addon.id}
                        className={`flex items-start p-4 rounded-xl border cursor-pointer transition-all ${
                          formData.addons.includes(addon.id) || addon.included
                            ? 'border-[#F47920] bg-[#F47920]/5' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Checkbox
                          checked={formData.addons.includes(addon.id) || addon.included}
                          onCheckedChange={() => !addon.included && toggleAddon(addon.id)}
                          disabled={addon.included}
                          className="mr-4 mt-1"
                        />
                        <div className="flex-1">
                          <p className="font-medium">{addon.name}</p>
                          {addon.description && (
                            <p className="text-sm text-slate-500">{addon.description}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">Art.-Nr.: {addon.article_number}</p>
                          {addon.price_info_url && (
                            <a 
                              href={addon.price_info_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-[#F47920] hover:underline mt-1 inline-block"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Preisliste & Einzelverbindungspreise →
                            </a>
                          )}
                        </div>
                        <p className="font-semibold text-right ml-4">
                          {addon.included ? (
                            <span className="text-green-600">Inklusive</span>
                          ) : addon.monthly_price > 0 ? (
                            `+${formatCurrency(addon.monthly_price)}/Monat`
                          ) : (
                            'Kostenlos'
                          )}
                        </p>
                      </label>
                    ))}
                  </div>

                  {/* Router Option */}
                  <div className="mt-6 pt-6 border-t">
                    <Label className="text-base font-semibold">Hardware</Label>
                    <label
                      className={`flex items-start p-4 mt-3 rounded-xl border cursor-pointer transition-all ${
                        formData.include_router
                          ? 'border-[#F47920] bg-[#F47920]/5' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={formData.include_router}
                        onCheckedChange={(checked) => updateFormData('include_router', checked)}
                        className="mr-4 mt-1"
                        data-testid="include-router"
                      />
                      <div className="flex-1">
                        <p className="font-medium">2x FRITZ!Box Router</p>
                        <p className="text-sm text-slate-500">Glasfaser-Router + VDSL-Backup-Router inklusive</p>
                      </div>
                      <p className="font-semibold text-right ml-4 text-green-600">Inklusive</p>
                    </label>
                    {!formData.include_router && (
                      <p className="mt-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
                        Hinweis: Der Kunde nutzt eigene Hardware. Bitte sicherstellen, dass die Hardware kompatibel ist.
                      </p>
                    )}
                  </div>

                  {/* Free Installation Option */}
                  <div className="mt-6 pt-6 border-t">
                    <Label className="text-base font-semibold">Installationskosten</Label>
                    <div className="mt-3 p-4 rounded-xl border border-slate-200 bg-slate-50">
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <p className="font-medium mb-1">Einmalige Kosten anpassen</p>
                          <p className="text-sm text-slate-500">
                            Standard: {formatCurrency(calculateTotals().defaultSetup)} 
                            {formData.contract_type === '24m' ? ' (24 Monate)' : ' (Flex)'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder={calculateTotals().defaultSetup.toFixed(2)}
                            value={formData.custom_setup_fee !== null ? formData.custom_setup_fee : ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') {
                                updateFormData('custom_setup_fee', null);
                              } else {
                                updateFormData('custom_setup_fee', parseFloat(value) || 0);
                              }
                            }}
                            className="w-32 text-right"
                            data-testid="custom-setup-fee"
                          />
                          <span className="text-slate-600">€</span>
                        </div>
                      </div>
                      {formData.custom_setup_fee !== null && (
                        <div className="mt-3 flex items-center justify-between">
                          <p className="text-sm text-amber-600">
                            Individueller Betrag: <strong>{formatCurrency(formData.custom_setup_fee)}</strong>
                            {formData.custom_setup_fee === 0 && ' (Kostenlos)'}
                          </p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => updateFormData('custom_setup_fee', null)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            Zurücksetzen
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </>
        )}

        {/* Step 2: Customer Data */}
        {step === 2 && (
          <>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Kundendaten</CardTitle>
              <CardDescription>Geben Sie die Kontakt- und Adressdaten des Kunden ein</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Personal Data */}
              <div className="grid gap-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <Label htmlFor="salutation">Anrede *</Label>
                    <Select
                      value={formData.customer.salutation}
                      onValueChange={(value) => updateFormData('customer.salutation', value)}
                    >
                      <SelectTrigger id="salutation" data-testid="customer-salutation">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Herr">Herr</SelectItem>
                        <SelectItem value="Frau">Frau</SelectItem>
                        <SelectItem value="Divers">Divers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1 sm:col-span-3">
                    <Label htmlFor="company">Firma (optional)</Label>
                    <Input
                      id="company"
                      value={formData.customer.company_name}
                      onChange={(e) => updateFormData('customer.company_name', e.target.value)}
                      placeholder="Firmenname"
                      data-testid="customer-company"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">Vorname *</Label>
                    <Input
                      id="first_name"
                      value={formData.customer.first_name}
                      onChange={(e) => updateFormData('customer.first_name', e.target.value)}
                      placeholder="Vorname"
                      required
                      data-testid="customer-first-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Nachname *</Label>
                    <Input
                      id="last_name"
                      value={formData.customer.last_name}
                      onChange={(e) => updateFormData('customer.last_name', e.target.value)}
                      placeholder="Nachname"
                      required
                      data-testid="customer-last-name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">E-Mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.customer.email}
                      onChange={(e) => updateFormData('customer.email', e.target.value)}
                      placeholder="kunde@example.de"
                      required
                      data-testid="customer-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Telefon *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.customer.phone}
                      onChange={(e) => updateFormData('customer.phone', e.target.value)}
                      placeholder="+49 123 456789"
                      required
                      data-testid="customer-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="birth_date">Geburtsdatum *</Label>
                  <Input
                    id="birth_date"
                    type="date"
                    value={formData.customer.birth_date}
                    onChange={(e) => updateFormData('customer.birth_date', e.target.value)}
                    required
                    data-testid="customer-birthdate"
                  />
                </div>
              </div>

              {/* Service Address */}
              <div className="pt-6 border-t space-y-4">
                <h3 className="font-semibold text-slate-900">Anschlussadresse</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="service_street">Straße *</Label>
                    <Input
                      id="service_street"
                      value={formData.customer.service_street}
                      onChange={(e) => updateFormData('customer.service_street', e.target.value)}
                      placeholder="Straße"
                      required
                      data-testid="customer-service-street"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_house_number">Hausnr. *</Label>
                    <Input
                      id="service_house_number"
                      value={formData.customer.service_house_number}
                      onChange={(e) => updateFormData('customer.service_house_number', e.target.value)}
                      placeholder="Nr."
                      required
                      data-testid="customer-service-house"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="service_postal_code">PLZ *</Label>
                    <Input
                      id="service_postal_code"
                      value={formData.customer.service_postal_code}
                      onChange={(e) => updateFormData('customer.service_postal_code', e.target.value)}
                      placeholder="PLZ"
                      required
                      data-testid="customer-service-plz"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="service_city">Ort *</Label>
                    <Input
                      id="service_city"
                      value={formData.customer.service_city}
                      onChange={(e) => updateFormData('customer.service_city', e.target.value)}
                      placeholder="Ort"
                      required
                      data-testid="customer-service-city"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="pt-6 border-t space-y-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="billing_same"
                    checked={formData.customer.billing_same_as_service}
                    onCheckedChange={(checked) => updateFormData('customer.billing_same_as_service', checked)}
                    data-testid="customer-billing-same"
                  />
                  <Label htmlFor="billing_same" className="cursor-pointer">
                    Rechnungsadresse ist gleich der Anschlussadresse
                  </Label>
                </div>

                {!formData.customer.billing_same_as_service && (
                  <div className="space-y-4 pl-6 border-l-2 border-slate-200">
                    <h3 className="font-semibold text-slate-900">Rechnungsadresse</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="billing_street">Straße *</Label>
                        <Input
                          id="billing_street"
                          value={formData.customer.billing_street}
                          onChange={(e) => updateFormData('customer.billing_street', e.target.value)}
                          placeholder="Straße"
                          data-testid="customer-billing-street"
                        />
                      </div>
                      <div>
                        <Label htmlFor="billing_house_number">Hausnr. *</Label>
                        <Input
                          id="billing_house_number"
                          value={formData.customer.billing_house_number}
                          onChange={(e) => updateFormData('customer.billing_house_number', e.target.value)}
                          placeholder="Nr."
                          data-testid="customer-billing-house"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="billing_postal_code">PLZ *</Label>
                        <Input
                          id="billing_postal_code"
                          value={formData.customer.billing_postal_code}
                          onChange={(e) => updateFormData('customer.billing_postal_code', e.target.value)}
                          placeholder="PLZ"
                          data-testid="customer-billing-plz"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="billing_city">Ort *</Label>
                        <Input
                          id="billing_city"
                          value={formData.customer.billing_city}
                          onChange={(e) => updateFormData('customer.billing_city', e.target.value)}
                          placeholder="Ort"
                          data-testid="customer-billing-city"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Preferred Date & Notes */}
              <div className="pt-6 border-t space-y-4">
                <div>
                  <Label htmlFor="preferred_date">Wunschtermin Schaltung (optional)</Label>
                  <Input
                    id="preferred_date"
                    type="date"
                    value={formData.preferred_date}
                    onChange={(e) => updateFormData('preferred_date', e.target.value)}
                    data-testid="preferred-date"
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Anmerkungen (optional)</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => updateFormData('notes', e.target.value)}
                    placeholder="Weitere Informationen zur Bestellung..."
                    rows={3}
                    data-testid="notes"
                  />
                </div>
              </div>
            </CardContent>
          </>
        )}

        {/* Step 3: SEPA */}
        {step === 3 && (
          <>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>SEPA-Lastschriftmandat</CardTitle>
              <CardDescription>Bankverbindung für die Zahlungsabwicklung</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-[#F47920] mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Zahlungsempfänger: nexiu GmbH</p>
                    <p className="text-sm text-slate-600 mt-1">
                      Der Kunde ermächtigt die nexiu GmbH, Zahlungen von seinem Konto mittels 
                      SEPA-Lastschrift einzuziehen.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="account_holder">Kontoinhaber *</Label>
                  <Input
                    id="account_holder"
                    value={formData.sepa.account_holder}
                    onChange={(e) => updateFormData('sepa.account_holder', e.target.value)}
                    placeholder="Name des Kontoinhabers"
                    required
                    data-testid="sepa-account-holder"
                  />
                </div>
                <div>
                  <Label htmlFor="iban">IBAN *</Label>
                  <Input
                    id="iban"
                    value={formData.sepa.iban}
                    onChange={(e) => updateFormData('sepa.iban', e.target.value.toUpperCase())}
                    placeholder="DE89 3704 0044 0532 0130 00"
                    required
                    data-testid="sepa-iban"
                  />
                </div>
                <div>
                  <Label htmlFor="bic">BIC (optional)</Label>
                  <Input
                    id="bic"
                    value={formData.sepa.bic}
                    onChange={(e) => updateFormData('sepa.bic', e.target.value.toUpperCase())}
                    placeholder="COBADEFFXXX"
                    data-testid="sepa-bic"
                  />
                </div>
              </div>
            </CardContent>
          </>
        )}

        {/* Step 4: Summary & Confirmation */}
        {step === 4 && (
          <>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Zusammenfassung & Bestätigung</CardTitle>
              <CardDescription>Überprüfen Sie die Bestelldaten und bestätigen Sie die rechtlichen Hinweise</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Order Summary */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Product */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-semibold text-slate-900 mb-3">Produkt</h3>
                  <p className="font-medium">{selectedProduct?.name}</p>
                  <p className="text-sm text-slate-500">
                    {formData.contract_type === '24m' ? '24 Monate Laufzeit' : 'Ohne Mindestlaufzeit'}
                  </p>
                  {formData.addons.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <p className="text-sm font-medium text-slate-600">Zusatzoptionen:</p>
                      {formData.addons.map(addonId => {
                        const addon = addons.find(a => a.id === addonId);
                        return addon && (
                          <p key={addonId} className="text-sm text-slate-500">+ {addon.name}</p>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Customer */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-semibold text-slate-900 mb-3">Kunde</h3>
                  <p className="font-medium">
                    {formData.customer.salutation} {formData.customer.first_name} {formData.customer.last_name}
                  </p>
                  <p className="text-sm text-slate-500">{formData.customer.email}</p>
                  <p className="text-sm text-slate-500">{formData.customer.phone}</p>
                  <p className="text-sm text-slate-500 mt-2">
                    {formData.customer.service_street} {formData.customer.service_house_number}<br />
                    {formData.customer.service_postal_code} {formData.customer.service_city}
                  </p>
                </div>
              </div>

              {/* Pricing */}
              <div className="p-4 bg-[#F47920]/5 rounded-xl border border-[#F47920]/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-600">Monatliche Kosten:</span>
                  <span className="font-semibold text-lg">{formatCurrency(totals.monthly)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Einmalige Kosten:</span>
                  <span className="font-semibold text-lg">{formatCurrency(totals.setup)}</span>
                </div>
              </div>

              {/* Legal Checkboxes */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="font-semibold text-slate-900">Rechtliche Bestätigungen</h3>
                
                {/* Documents Section - Product and contract specific */}
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 mb-4">
                  <p className="font-medium text-slate-900 mb-3">Vertragsunterlagen zum Download:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <a href={legalDocs.agb} target="_blank" rel="noopener noreferrer" className="text-[#F47920] hover:underline flex items-center gap-2">
                      <FileCheck className="w-4 h-4" /> AGB
                    </a>
                    <a 
                      href={
                        formData.contract_type === '24m' 
                          ? legalDocs[`pib_${formData.product_id?.replace('-', '_')}_24m`] 
                          : legalDocs[`pib_${formData.product_id?.replace('-', '_')}`]
                        || legalDocs.pib_safeconnect_300
                      } 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-[#F47920] hover:underline flex items-center gap-2"
                    >
                      <FileCheck className="w-4 h-4" /> Produktinformationsblatt ({selectedProduct?.name} {formData.contract_type === '24m' ? '24M' : ''})
                    </a>
                    <div className="flex items-center gap-2 text-slate-500">
                      <FileCheck className="w-4 h-4" /> Vorvertragliche Informationen <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">(wird mit Bestellung generiert)</span>
                    </div>
                    <a href={legalDocs.widerrufsrecht} target="_blank" rel="noopener noreferrer" className="text-[#F47920] hover:underline flex items-center gap-2">
                      <FileCheck className="w-4 h-4" /> Widerrufsrecht
                    </a>
                    <a href={legalDocs.datenschutzerklaerung} target="_blank" rel="noopener noreferrer" className="text-[#F47920] hover:underline flex items-center gap-2">
                      <FileCheck className="w-4 h-4" /> Datenschutzerklärung
                    </a>
                    <a href={legalDocs.datenschutzhinweis} target="_blank" rel="noopener noreferrer" className="text-[#F47920] hover:underline flex items-center gap-2">
                      <FileCheck className="w-4 h-4" /> Datenschutzhinweis
                    </a>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 mb-4">
                  <p className="font-medium text-slate-900 mb-2">Ihre Bestellung im Überblick:</p>
                  <div className="text-sm text-slate-600 space-y-1">
                    <p><strong>Produkt:</strong> {selectedProduct?.name}</p>
                    <p><strong>Geschwindigkeit:</strong> {selectedProduct?.download_speed} / {selectedProduct?.upload_speed}</p>
                    <p><strong>Vertragslaufzeit:</strong> {formData.contract_type === '24m' ? '24 Monate' : 'Ohne Mindestlaufzeit'}</p>
                    <p><strong>Router:</strong> {formData.include_router ? '2x FRITZ!Box inklusive' : 'Kein Router (eigene Hardware)'}</p>
                    {formData.addons.length > 0 && (
                      <p><strong>Zusatzoptionen:</strong> {formData.addons.map(id => addons.find(a => a.id === id)?.name).filter(Boolean).join(', ')}</p>
                    )}
                    {formData.custom_setup_fee !== null && (
                      <p><strong>Installationskosten:</strong> <span className={formData.custom_setup_fee === 0 ? 'text-green-600' : 'text-amber-600'}>
                        {formData.custom_setup_fee === 0 ? 'Kostenlos' : `${formatCurrency(formData.custom_setup_fee)} (individuell)`}
                      </span></p>
                    )}
                    {formData.notes && (
                      <div className="mt-2 pt-2 border-t border-slate-200">
                        <p><strong>Anmerkungen/Sondervereinbarungen:</strong></p>
                        <p className="whitespace-pre-wrap bg-amber-50 p-2 rounded mt-1 border border-amber-200">{formData.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={formData.agb_accepted}
                    onCheckedChange={(checked) => updateFormData('agb_accepted', checked)}
                    className="mt-1"
                    data-testid="checkbox-agb"
                  />
                  <span className="text-sm text-slate-600">
                    <span className="text-red-500">*</span> Ich akzeptiere die{' '}
                    <a href={legalDocs.agb} target="_blank" rel="noopener noreferrer" className="text-[#F47920] underline">Allgemeinen Geschäftsbedingungen</a>{' '}
                    und <a 
                      href={
                        formData.contract_type === '24m' 
                          ? legalDocs[`pib_${formData.product_id?.replace('-', '_')}_24m`] 
                          : legalDocs[`pib_${formData.product_id?.replace('-', '_')}`]
                        || legalDocs.pib_safeconnect_300
                      } 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-[#F47920] underline"
                    >Leistungsbeschreibungen</a>.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={formData.widerruf_accepted}
                    onCheckedChange={(checked) => updateFormData('widerruf_accepted', checked)}
                    className="mt-1"
                    data-testid="checkbox-widerruf"
                  />
                  <span className="text-sm text-slate-600">
                    <span className="text-red-500">*</span> Ich habe die{' '}
                    <a href={legalDocs.widerrufsrecht} target="_blank" rel="noopener noreferrer" className="text-[#F47920] underline">Widerrufsbelehrung</a>{' '}
                    und <a href={legalDocs.vorvertragliche_informationen} target="_blank" rel="noopener noreferrer" className="text-[#F47920] underline">vorvertraglichen Informationen</a>{' '}
                    erhalten und zur Kenntnis genommen.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={formData.datenschutz_accepted}
                    onCheckedChange={(checked) => updateFormData('datenschutz_accepted', checked)}
                    className="mt-1"
                    data-testid="checkbox-datenschutz"
                  />
                  <span className="text-sm text-slate-600">
                    <span className="text-red-500">*</span> Ich habe die{' '}
                    <a href={legalDocs.datenschutzerklaerung} target="_blank" rel="noopener noreferrer" className="text-[#F47920] underline">Datenschutzinformationen</a>{' '}
                    zur Kenntnis genommen.
                  </span>
                </label>

                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-slate-700 mb-3">Marketing-Einwilligungen (optional)</p>
                  
                  <label className="flex items-start gap-3 cursor-pointer mb-2">
                    <Checkbox
                      checked={formData.marketing_email}
                      onCheckedChange={(checked) => updateFormData('marketing_email', checked)}
                      className="mt-1"
                      data-testid="checkbox-marketing-email"
                    />
                    <span className="text-sm text-slate-600">
                      Ich möchte per E-Mail über Angebote und Neuigkeiten informiert werden.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={formData.marketing_phone}
                      onCheckedChange={(checked) => updateFormData('marketing_phone', checked)}
                      className="mt-1"
                      data-testid="checkbox-marketing-phone"
                    />
                    <span className="text-sm text-slate-600">
                      Ich möchte telefonisch über Angebote und Neuigkeiten informiert werden.
                    </span>
                  </label>
                </div>
              </div>
            </CardContent>
          </>
        )}

        {/* Navigation */}
        <div className="px-6 py-4 border-t bg-slate-50 rounded-b-lg flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(prev => Math.max(prev - 1, 1))}
            disabled={step === 1}
            data-testid="step-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>

          {/* Price display */}
          {formData.product_id && (
            <div className="hidden sm:block text-center">
              <p className="text-sm text-slate-500">Monatlich</p>
              <p className="text-xl font-bold text-slate-900">{formatCurrency(totals.monthly)}</p>
            </div>
          )}

          {step < 4 ? (
            <Button
              onClick={handleNext}
              className="bg-[#F47920] hover:bg-[#E06810]"
              data-testid="step-next-btn"
            >
              Weiter
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !validateStep(4)}
              className="bg-[#F47920] hover:bg-[#E06810]"
              data-testid="submit-order-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wird erstellt...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Bestellung erstellen
                </>
              )}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default NewOrder;
